import * as React from "react";
import { PERMISSIONS } from "@/constants";
import {
	Alert,
	Box,
	Button,
	Chip,
	Divider,
	Drawer,
	IconButton,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useColorScheme,
} from "@mui/material";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { PlayCircleIcon } from "@phosphor-icons/react/dist/ssr/PlayCircle";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";
import { XCircleIcon } from "@phosphor-icons/react/dist/ssr/XCircle";

import { Conversation } from "@/hooks/api/use-call-logs";
import { CellVerdict, useOverrideVerdict, useRunCell, useTranslateCell } from "@/hooks/api/use-proving-ground";
import { CallLogAudioPlayer } from "@/components/call-log-modal/call-log-audio-player";
import { ConversationTranscript } from "@/components/call-log-modal/conversation-transcript";
import { MessageBubble } from "@/components/call-log-modal/message-bubble";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { useAuth } from "@/components/core/auth-provider";

import { METRIC_KIND_LABEL, verdictInk } from "../shared";

/**
 * One check, with the judge's verdict and a place for a person to disagree.
 *
 * The human verdict never replaces the machine one — both stay visible, and a disagreement
 * is called out rather than smoothed over. Those pairs are the only measure of whether the
 * judge is worth trusting, and hiding them would throw away the evidence.
 */
function VerdictRow({
	verdict,
	cellId,
	selected,
	onSelect,
	canOverride,
}: {
	verdict: CellVerdict;
	cellId: number;
	selected: boolean;
	onSelect: () => void;
	/**
	 * False for a manager. Overruling the judge changes the record a published run is read
	 * from, so it belongs to whoever owns the configuration - the API refuses it either way,
	 * and offering a button that 403s is worse than not offering it.
	 */
	canOverride: boolean;
}): React.JSX.Element {
	const override = useOverrideVerdict(cellId);
	const [noting, setNoting] = React.useState(false);
	const [note, setNote] = React.useState("");

	const effective = verdict.humanPassed ?? verdict.passed;

	const submit = async (passed: boolean | null) => {
		await override.mutateAsync({ verdictId: verdict.id, passed, note: note.trim() || undefined });
		setNoting(false);
		setNote("");
	};

	return (
		<Box
			onClick={onSelect}
			sx={{
				p: 1.25,
				borderRadius: 1,
				cursor: "pointer",
				bgcolor: selected ? "action.selected" : "transparent",
				"&:hover": { bgcolor: selected ? "action.selected" : "action.hover" },
			}}
		>
			<Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
				<Box sx={{ pt: 0.25 }}>
					{/* The icon inherits currentColor, so it follows the same scheme-aware ink as the
					    text rather than a fixed `.main` that measures 2.5:1 on a light card. */}
					{verdict.notApplicable ? (
						<Chip size="small" label="n/a" variant="outlined" />
					) : (
						<Box component="span" sx={[{ display: "inline-flex" }, verdictInk(effective ? "pass" : "gate")]}>
							{effective ? <CheckCircleIcon size={18} weight="fill" /> : <XCircleIcon size={18} weight="fill" />}
						</Box>
					)}
				</Box>
				<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{verdict.metricName}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{METRIC_KIND_LABEL[verdict.kind] ?? verdict.kind}
						{verdict.value === null ? "" : ` · ${verdict.value.toFixed(2)}`}
					</Typography>
					{verdict.disagrees ? (
						<Chip
							size="small"
							color="warning"
							variant="outlined"
							label={`You overruled the judge: ${verdict.humanPassed ? "pass" : "fail"}`}
							sx={{ mt: 0.5, display: "flex", width: "max-content" }}
						/>
					) : null}
				</Box>
			</Stack>

			{selected ? (
				<Box sx={{ mt: 1, pl: 3.5 }} onClick={(event) => event.stopPropagation()}>
					{verdict.reasoning ? (
						<Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>
							{verdict.reasoning}
						</Typography>
					) : null}

					{verdict.humanNote ? (
						<Alert severity="info" sx={{ mb: 1 }}>
							{verdict.humanNote}
						</Alert>
					) : null}

					{canOverride ? noting ? (
						<Stack spacing={1}>
							<TextField
								size="small"
								fullWidth
								multiline
								minRows={2}
								placeholder="Why is the judge wrong here?"
								value={note}
								onChange={(event) => setNote(event.target.value)}
							/>
							<Stack direction="row" spacing={1}>
								<Button size="small" variant="contained" color="success" onClick={() => submit(true)}>
									Mark pass
								</Button>
								<Button size="small" variant="contained" color="error" onClick={() => submit(false)}>
									Mark fail
								</Button>
								<Button size="small" color="secondary" onClick={() => setNoting(false)}>
									Cancel
								</Button>
							</Stack>
						</Stack>
					) : (
						<Stack direction="row" spacing={1}>
							<Button size="small" variant="outlined" onClick={() => setNoting(true)} disabled={override.isPending}>
								{verdict.humanPassed === null ? "Disagree" : "Change my verdict"}
							</Button>
							{verdict.humanPassed === null ? null : (
								<Button size="small" color="secondary" onClick={() => submit(null)} disabled={override.isPending}>
									Clear
								</Button>
							)}
						</Stack>
					) : null}
				</Box>
			) : null}
		</Box>
	);
}

/**
 * Cells recorded before the conversation was linked still have their own transcript, so they
 * are shaped into the same message rows rather than left blank or rendered by a second set of
 * bubbles that would drift from the real ones. The timings are the persona runner's offsets,
 * which is the only clock those rows ever had.
 */
function legacyMessages(transcript: { speaker: string; content: string; atMs: number }[] | undefined): Conversation[] {
	return (transcript ?? []).map(
		(turn, index, all) =>
			({
				id: `legacy-${index}`,
				role: turn.speaker === "agent" ? "assistant" : "user",
				content: turn.content,
				completion: "",
				createdAt: new Date(turn.atMs).toISOString(),
				metadata: { start: turn.atMs, end: all[index + 1]?.atMs ?? turn.atMs },
				usage: null,
			}) as unknown as Conversation
	);
}

export function CellDrawer({ cellId, onClose }: { cellId: number | null; onClose: () => void }): React.JSX.Element {
	const { data: cell, isLoading } = useRunCell(cellId ?? undefined);
	const { permissions } = useAuth();
	// A manager reads a published run; they do not get to rewrite its verdicts. Same
	// distinction the section itself is gated on: they hold `provingRun`, not `agent`.
	const canOverride = Boolean(permissions[PERMISSIONS.agent]);

	const translate = useTranslateCell(cellId ?? undefined);
	const [showEnglish, setShowEnglish] = React.useState(false);
	// Whatever has already been translated, plus whatever this press just produced - the cell
	// refetches after the mutation, but reading the result directly avoids a blank beat.
	const translation = { ...cell?.transcriptTranslation, ...translate.data?.translation };
	const hasTranslation = Object.keys(translation).length > 0;
	const [selected, setSelected] = React.useState<number | null>(null);
	const [showAudioPlayer, setShowAudioPlayer] = React.useState(false);
	const [currentTime, setCurrentTime] = React.useState(0);
	const { colorScheme = "light" } = useColorScheme();

	const currentTimeRef = React.useRef(0);
	const previousUpdateTimeRef = React.useRef(0);

	React.useEffect(() => {
		// Open on the first thing that failed — the reason anyone opened this at all. Only when
		// nothing valid is selected: overruling a verdict refetches the cell, and re-seeding on
		// every refetch collapsed the row the reviewer was in the middle of writing a note on.
		setSelected((current) => {
			if (current && cell?.results.some((result) => result.id === current)) return current;
			const failing = cell?.results.find((result) => (result.humanPassed ?? result.passed) === false);
			return failing?.id ?? cell?.results[0]?.id ?? null;
		});
	}, [cell?.id, cell?.results]);

	// Each cell is a different call. Carrying a playhead across would light up the wrong turn.
	React.useEffect(() => {
		setShowEnglish(false);
		setShowAudioPlayer(false);
		setCurrentTime(0);
		currentTimeRef.current = 0;
		previousUpdateTimeRef.current = 0;
	}, [cellId]);

	return (
		<Drawer
			anchor="right"
			open={Boolean(cellId)}
			onClose={onClose}
			slotProps={{ paper: { sx: { width: { xs: "100%", md: 1100 } } } }}
		>
			<Box sx={{ position: "relative", height: "100%", display: "flex", flexDirection: "column" }}>
				<ComponentLoader loading={isLoading} />

				<Stack direction="row" sx={{ alignItems: "center", p: 2, pb: 1.5 }} spacing={1}>
					<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
						<Typography variant="h6" noWrap>
							{cell?.scenarioName ?? "Call"}
						</Typography>
						<Typography variant="body2" color="text.secondary" noWrap>
							{cell?.personaName}
						</Typography>
					</Box>
					<Tooltip title={showAudioPlayer ? "Hide recording" : "Show recording"}>
						{/* Span so the tooltip still shows on a text run, where there is nothing to play. */}
						<Box component="span">
							<IconButton
								onClick={() => setShowAudioPlayer(!showAudioPlayer)}
								disabled={!cell?.hasRecording}
								size="small"
							>
								<PlayCircleIcon
									size={20}
									weight="fill"
									color={showAudioPlayer ? "var(--mui-palette-primary-main)" : "var(--mui-palette-info-light)"}
								/>
							</IconButton>
						</Box>
					</Tooltip>
					<IconButton onClick={onClose}>
						<XIcon size={18} />
					</IconButton>
				</Stack>

				<Divider />

				{cell?.calendarReport ? (
					<Box sx={{ p: 2 }}>
						<Alert severity="info">
							Simulated calendar — no Google events or invitations were sent. {cell.calendarReport.bookings.length}{" "}
							booking(s), {cell.calendarReport.attempts} attempt(s).
						</Alert>
						{cell.calendarReport.bookings.map((booking, index) => (
							<Typography key={index} variant="body2" sx={{ mt: 1 }}>
								{booking.title} — {booking.status}.{" "}
								{new Date(booking.start).toLocaleString(undefined, { timeZone: cell.calendarReport!.timezone })} (
								{cell.calendarReport!.timezone}). Attendees: {booking.attendees.join(", ") || "None"}.
							</Typography>
						))}
					</Box>
				) : null}

				{cell?.error ? (
					<Alert severity="error" sx={{ m: 2 }}>
						{cell.error}
					</Alert>
				) : null}

				{/* A truncated call is not a failed one, and nothing else on this screen says so.
				    Every check about an outcome fails when the call was cut before the outcome
				    could happen, which reads as an agent fault unless it is called out. */}
				{cell?.endedBy === "timeout" ? (
					<Alert severity="warning" sx={{ m: 2 }}>
						This call hit the time limit rather than ending on its own, so anything scored below was judged on a
						conversation that never finished. Raise <strong>Maximum call length</strong> in Proving Ground settings and
						run it again.
					</Alert>
				) : null}

				<Stack direction="row" spacing={2} sx={{ px: 2, py: 1.5, flexWrap: "wrap" }}>
					<Metric label="Turns" value={String(cell?.turns ?? 0)} />
					<Metric label="Duration" value={`${Math.round((cell?.durationMs ?? 0) / 1000)}s`} />
					<Metric label="Ended by" value={cell?.endedBy ?? "—"} />
					{cell?.agentTalkRatio !== null && cell?.agentTalkRatio !== undefined ? (
						<Metric label="Agent talk time" value={`${Math.round(cell.agentTalkRatio * 100)}%`} />
					) : null}
					{cell?.latencyP95Ms !== null && cell?.latencyP95Ms !== undefined ? (
						<Tooltip
							arrow
							title={`Median ${cell.latencyP50Ms}ms, worst ${cell.latencyMaxMs}ms, over ${cell.latencySamples} ${
								cell.latencySamples === 1 ? "turn" : "turns"
							}. Measured from the caller falling silent to the agent starting to speak.`}
						>
							<Box>
								<Metric label="Reply latency p95" value={`${cell.latencyP95Ms}ms`} />
							</Box>
						</Tooltip>
					) : null}
				</Stack>

				<CallLogAudioPlayer
					recordingUrl={cell?.hasRecording ? `/proving-ground/runs/cells/${cell.id}/recording` : null}
					show={showAudioPlayer}
					onTimeUpdate={(time) => {
						currentTimeRef.current = time;

						const currentTimeMs = time * 1000;
						if (Math.abs(currentTimeMs - previousUpdateTimeRef.current) >= 200) {
							previousUpdateTimeRef.current = currentTimeMs;
							setCurrentTime(time);
						}
					}}
				/>
				{showAudioPlayer && cell?.hasRecording ? (
					<Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1 }}>
						Stereo — caller on the left channel, agent on the right.
					</Typography>
				) : null}

				<Divider />

				<Box
					sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, flex: "1 1 auto", minHeight: 0 }}
				>
					<Box sx={{ borderRight: { md: 1 }, borderColor: "divider", overflowY: "auto", p: 1 }}>
						<Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
							Scorecard
						</Typography>
						{(cell?.results ?? []).map((verdict) => (
							<VerdictRow
								key={verdict.id}
								verdict={verdict}
								cellId={cell!.id}
								selected={selected === verdict.id}
								onSelect={() => setSelected(verdict.id)}
								canOverride={canOverride}
							/>
						))}
						{cell && cell.results.length === 0 ? (
							<Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
								Not scored.
							</Typography>
						) : null}
					</Box>

					<Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflowY: "auto" }}>
						<Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", px: 2, pt: 2 }}>
							<Typography variant="overline" color="text.secondary">
								Transcript
							</Typography>
							{/* Offered on every call, because a Latin-script language reads as English to a
							    glance and the reader is the one who knows whether they need it. Translated
							    once and kept, so flipping back and forth is free after the first press. */}
							<ToggleButtonGroup
								size="small"
								exclusive
								value={showEnglish ? "en" : "original"}
								onChange={(_event, value) => {
									if (!value) return;
									setShowEnglish(value === "en");
									if (value === "en" && !hasTranslation && !translate.isPending) translate.mutate();
								}}
							>
								<ToggleButton value="original" sx={{ py: 0.25, px: 1 }}>
									Original
								</ToggleButton>
								<ToggleButton value="en" sx={{ py: 0.25, px: 1 }} disabled={translate.isPending}>
									{translate.isPending ? "Translating…" : "English"}
								</ToggleButton>
							</ToggleButtonGroup>
						</Stack>
						{/* The same component call logs use, so a simulated call is read exactly the way a
						    real one is. Cells from before the conversation was linked fall back to the
						    transcript they recorded, rendered through the same bubbles. */}
						{cell?.conversationId ? (
							<ConversationTranscript
								conversationId={cell.conversationId}
								currentTime={currentTime}
								currentTimeRef={currentTimeRef}
								active={Boolean(cellId)}
								emptyMessage="No transcript recorded for this call"
								translation={showEnglish ? translation : undefined}
							/>
						) : (
							<Stack spacing={1} sx={{ p: 2, pt: 1 }}>
								{legacyMessages(cell?.transcript).map((message) => (
									<MessageBubble
										key={message.id}
										message={
											showEnglish && translation[message.id]
												? { ...message, content: translation[message.id], completion: "" }
												: message
										}
										colorScheme={colorScheme}
										currentTime={currentTime}
										currentTimeRef={currentTimeRef}
									/>
								))}
							</Stack>
						)}
						{cell?.hangUpReason ? (
							<Alert severity="info" sx={{ m: 2, mt: 0 }}>
								Caller hung up: {cell.hangUpReason}
							</Alert>
						) : null}
					</Box>
				</Box>
			</Box>
		</Drawer>
	);
}

function Metric({ label, value }: { label: string; value: string }): React.JSX.Element {
	return (
		<Box>
			<Typography variant="caption" color="text.secondary" sx={{ display: "block", textTransform: "uppercase" }}>
				{label}
			</Typography>
			<Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
				{value}
			</Typography>
		</Box>
	);
}
